import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Bell, Flame, ClipboardCheck, Lock, Trophy, Sparkles, ChevronRight } from "lucide-react";

const ACTIVITY_TARGET = 7;

/* Builds contextual notifications from live dashboard data. */
function buildNotifications(dash) {
  if (!dash) return [];
  const list = [];
  const step = dash.current_step || 1;

  if (dash.graduated) {
    list.push({ id: "grad", icon: Trophy, color: "#F59E0B", title: "Course complete", body: "View your final report and graduation.", to: "/graduation" });
    list.push({ id: "maint", icon: Sparkles, color: "#22D3EE", title: "Stay reset", body: "Keep your gains with the Maintenance plan.", to: "/maintenance-plan" });
    return list;
  }

  if (!dash.checked_in_today) {
    list.push({ id: "checkin", icon: ClipboardCheck, color: "#22D3EE", title: "Log today's check-in", body: "Track your pain to keep your trend accurate.", to: "/home" });
  }

  if (dash.streak > 0) {
    list.push({ id: "streak", icon: Flame, color: "#F59E0B", title: `${dash.streak}-day streak`, body: "Show up today to keep it alive.", to: "/home" });
  }

  const done = dash.activities_this_week ?? 0;
  const remaining = Math.max(0, ACTIVITY_TARGET - done);
  if (remaining > 0) {
    list.push({ id: "unlock", icon: Lock, color: "#A855F7", title: `Unlock Week ${Math.min(step + 1, 4)}`, body: `${remaining} more ${remaining === 1 ? "activity" : "activities"} this week.`, to: `/journey/${step}/daily-practice` });
  }

  if (list.length === 0) {
    list.push({ id: "all", icon: Sparkles, color: "#10B981", title: "You're all caught up", body: "Nice work today. Keep moving freely.", to: "/home" });
  }
  return list;
}

export default function NotificationBell({ dash }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const items = buildNotifications(dash);
  const unread = dash && !dash.graduated ? items.filter((i) => i.id !== "all").length : 0;

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="notification-bell"
        className="relative w-11 h-11 rounded-2xl bg-[#0A1530] border border-white/10 flex items-center justify-center hover:border-[#22D3EE]/40 transition-colors"
      >
        <Bell size={18} className="text-[#7C8FA8]" strokeWidth={1.8} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F59E0B] text-[#1A1206] text-[10px] font-bold flex items-center justify-center" data-testid="notification-count">
            {unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={{ duration: 0.18 }}
            className="absolute right-0 mt-2 w-72 z-[60] rounded-3xl border border-white/10 overflow-hidden bps-glass shadow-[0_24px_60px_rgba(0,0,0,0.55)]"
            data-testid="notification-panel"
          >
            <div className="px-4 py-3 border-b border-white/10 bps-eyebrow text-[#22D3EE]">Notifications</div>
            <div className="max-h-[60vh] overflow-y-auto">
              {items.map(({ id, icon: Icon, color, title, body, to }) => (
                <button
                  key={id}
                  onClick={() => { setOpen(false); navigate(to); }}
                  data-testid={`notification-${id}`}
                  className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors"
                >
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}1f`, border: `1px solid ${color}55` }}>
                    <Icon size={16} style={{ color }} strokeWidth={1.9} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white leading-tight">{title}</div>
                    <div className="text-[11px] text-[#9AAEC6] mt-0.5 leading-snug">{body}</div>
                  </div>
                  <ChevronRight size={14} className="text-[#7C8FA8] shrink-0" />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
